// Orders page › order detail: the BargainFox returns that touch this order
// (Portal-Order.dc.html). Shown for the original order (any part of a split
// order), a replacement or an exchange raised by a return. Each row names the
// request, its status and money, links to it on the Returns page, and lists
// its invoices (exchange top-ups) and the credit notes of any refunds of them,
// to open as PDFs.
//
// Renders nothing for an order with no returns, and stays silent when the
// lookup is refused (no admin.orders / admin.returns read) or fails: the panel
// is context, never a reason for the order modal to show an error.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Undo2, FileText, ExternalLink } from "lucide-react";
import { useTranslation } from "../../i18n";
import { OrderReturn, ReturnContact, returnsForOrder, openProtectedFile, invoicePdfPath, creditNotePdfPath, gbp } from "../../api/returns";
import { ContactList } from "./ReturnContacts";
import { StatusPill, problemLabel, resolutionLabel } from "./returnLabels";
import "../../styles/admin-returns.css";

const OrderReturnsPanel = ({ orderId, onOpenOrder }: { orderId: string; onOpenOrder?: (orderId: string) => void }) => {
    const { t, language } = useTranslation();
    const [rows, setRows] = useState<OrderReturn[] | null>(null);
    const [contacts, setContacts] = useState<ReturnContact[]>([]);
    const dateFmt = new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", { day: "numeric", month: "short", year: "numeric" });

    useEffect(() => {
        let alive = true;
        setRows(null);
        setContacts([]);
        returnsForOrder(orderId).then((r) => {
            if (!alive) return;
            setRows(r.ok && r.data ? r.data.returns : []);
            setContacts(r.ok && r.data && Array.isArray(r.data.contacts) ? r.data.contacts : []);
        });
        return () => { alive = false; };
    }, [orderId]);

    if (!rows || (rows.length === 0 && contacts.length === 0)) return null;

    const openDoc = async (path: string) => {
        const r = await openProtectedFile(path);
        if (r.notAvailable) toast.info(t("rt_not_available"));
        else if (!r.ok) toast.error(t("rt_file_failed"));
    };

    return (
        <section className="rt-order-panel" aria-label={t("rt_panel_title")}>
            <h3 className="rt-h3"><Undo2 size={14} /> {t("rt_panel_title")}</h3>
            {rows.map((r) => (
                <div key={r.reference} className="rt-order-panel-row">
                    <div className="rt-order-panel-main">
                        <span className="rt-order-panel-head">
                            <Link className="rt-ref-link rt-mono" to={`/admin/returns?tab=all&ref=${encodeURIComponent(r.reference)}`}>
                                {r.reference} <ExternalLink size={12} />
                            </Link>
                            <StatusPill status={r.status} t={t} />
                        </span>
                        <span className="rt-muted rt-small">
                            {t(`rt_relation_${r.relation}`)} · {problemLabel(t, r.problem)} · {resolutionLabel(t, r.resolution)} · {dateFmt.format(new Date(r.created_at))}
                            {r.refund_total_pence > 0 ? ` · ${t("rt_refund_amount", { amount: gbp(r.refund_total_pence) })}` : ""}
                            {r.payable_pence > 0 ? ` · ${t("rt_paid_amount", { amount: gbp(r.payable_pence) })}` : ""}
                        </span>
                        {onOpenOrder && [...r.replacement_order_ids, ...r.exchange_order_ids].filter((id) => id !== orderId).length > 0 && (
                            <span className="rt-links">
                                {[...r.replacement_order_ids, ...r.exchange_order_ids].filter((id) => id !== orderId).map((id) => (
                                    <button key={id} type="button" className="rt-chip" onClick={() => onOpenOrder(id)}>{id}</button>
                                ))}
                            </span>
                        )}
                    </div>
                    {(r.invoices.length > 0 || (r.credit_notes ?? []).length > 0) && (
                        <div className="rt-order-panel-invoices">
                            {r.invoices.map((inv) => (
                                <button key={inv.invoice_number} type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openDoc(invoicePdfPath(inv.invoice_number))}>
                                    <FileText size={14} /> {t("rp_pay_invoice", { number: inv.invoice_number })} · {gbp(inv.amount_pence)}
                                </button>
                            ))}
                            {(r.credit_notes ?? []).map((cn) => (
                                <button key={cn.credit_note_number} type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openDoc(creditNotePdfPath(cn.credit_note_number))}>
                                    <FileText size={14} /> {t("rp_pay_credit_note", { number: cn.credit_note_number, amount: gbp(cn.amount_pence) })}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            <ContactList contacts={contacts} />
        </section>
    );
};

export default OrderReturnsPanel;
